import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { X, Clock, MapPin, Video, Car, FileText, CheckCircle2, AlertTriangle, Ban, Check, Send, LogIn, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '../Button';
import * as agenda from '../../services/agendaService';
import type { MatrixItem } from '../../services/agendaService';
import {
  B2B_SOORT_LABEL,
  bevestigB2bAlsProfessional,
  dienB2bUrenIn,
  trekB2bIn,
  weigerB2bAlsProfessional,
  type B2bSoort,
} from '../../services/lopendeTrajectenApi';
import { intervisieApi } from '../../services/intervisieApi';
import { KLEUR_KLASSE, blokStijl } from './kleuren';

const fout = (err: unknown, standaard: string) => (isAxiosError(err) ? err.response?.data?.message : undefined) ?? standaard;
const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

/** Detailpaneel naast de matrix: wat het blok is en wat je ermee kunt. */
export const MatrixDetail: React.FC<{ item: MatrixItem; onSluiten: () => void; onGewijzigd: () => void }> = ({ item, onSluiten, onGewijzigd }) => {
  const { t } = useTranslation('agenda');
  const start = new Date(item.start);
  const eind = new Date(item.eind);
  const nu = Date.now();
  const begonnen = start.getTime() <= nu;

  const actie = (fn: () => Promise<unknown>, melding: string) => ({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(melding);
      onGewijzigd();
    },
    onError: (err: unknown) => toast.error(fout(err, t('matrix.detail.fout', 'Dat lukte niet'))),
  });

  const sluitTijd = useMutation(actie(() => Promise.all((item.meta.slot_ids as string[]).map((id) => agenda.sluitSlot(id))), t('matrix.detail.tijdGesloten', 'Open tijd gesloten')));
  const voltooid = useMutation(actie(() => agenda.registreerVoltooid(item.ref_id), t('matrix.detail.voltooid', 'Sessie afgerond')));
  const noShow = useMutation(actie(() => agenda.registreerNoShow(item.ref_id), t('matrix.detail.noShow', 'No-show geregistreerd')));
  const annuleer = useMutation(actie(() => agenda.annuleerAfspraak(item.ref_id, 'Geannuleerd door de professional'), t('matrix.detail.geannuleerd', 'Afspraak geannuleerd')));
  const bevestig = useMutation(actie(() => bevestigB2bAlsProfessional(item.ref_id), t('matrix.detail.geaccepteerd', 'Afspraak geaccepteerd')));
  const weiger = useMutation(actie(() => weigerB2bAlsProfessional(item.ref_id, window.prompt(t('matrix.detail.redenWeigering', 'Reden van weigering (optioneel):')) || undefined), t('matrix.detail.geweigerd', 'Afspraak geweigerd')));
  const trekIn = useMutation(actie(() => trekB2bIn(item.ref_id), t('matrix.detail.ingetrokken', 'Afspraak ingetrokken')));
  const dienIn = useMutation(actie(async () => {
    const invoer = window.prompt(t('matrix.detail.urenVraag', { defaultValue: 'Gewerkte uren (gepland: {{uren}})', uren: item.meta.uren_gepland }), String(item.meta.uren_gepland ?? ''));
    const uren = invoer ? Number(invoer.replace(',', '.')) : NaN;
    if (!(uren > 0)) throw new Error(t('matrix.detail.urenOngeldig', 'Geen geldig aantal uren'));
    return dienB2bUrenIn(item.ref_id, uren);
  }, t('matrix.detail.urenIngediend', 'Uren ingediend — de opdrachtgever fiatteert ze')));
  const verwijderBlokkade = useMutation(actie(() => agenda.verwijderBlokkade(item.ref_id), t('blokkade.verwijderd', 'Blokkade opgeheven.')));
  const deelnemen = useMutation({
    mutationFn: () => intervisieApi.joinTeamsSession(item.ref_id),
    onSuccess: (r) => window.open(r.join_url, '_blank', 'noopener,noreferrer'),
    onError: (err: unknown) => toast.error(fout(err, t('matrix.detail.fout', 'Dat lukte niet'))),
  });

  const statusLabel = (): string | null => {
    if (item.bron === 'medewerker') {
      return t(`matrix.status.medewerker.${item.status}`, {
        defaultValue: ({
          PENDING_APPROVAL: 'Verzoek van medewerker — wacht op jou',
          TEGENVOORSTEL: 'Wacht op bevestiging medewerker',
          SCHEDULED: 'Bevestigd',
          COMPLETED: 'Afgerond',
          NO_SHOW: 'No-show',
        } as Record<string, string>)[item.status ?? ''] ?? item.status,
      });
    }
    if (item.bron === 'b2b') {
      if (item.status === 'GEPLAND') {
        return item.meta.bevestiging_status === 'WACHT'
          ? item.meta.voorgesteld_door === 'employer'
            ? t('matrix.status.b2b.wachtOpJou', 'Uitnodiging van de opdrachtgever — wacht op jou')
            : t('matrix.status.b2b.wacht', 'Wacht op bevestiging')
          : t('matrix.status.b2b.bevestigd', 'Wederzijds bevestigd');
      }
      return t(`matrix.status.b2b.${item.status}`, {
        defaultValue: ({ UITGEVOERD: 'Uren ingediend', GEFIATTEERD: 'Gefiatteerd', GEFACTUREERD: 'Gefactureerd', AFGEWEZEN: 'Uren afgewezen' } as Record<string, string>)[item.status ?? ''] ?? item.status,
      });
    }
    return null;
  };

  const bronLabel = t(`matrix.bron.${item.bron}`, {
    defaultValue: ({
      beschikbaar: 'Beschikbaar tijdslot',
      medewerker: 'Medewerker-sessie',
      b2b: 'Corporate B2B-overleg',
      kennismaking: 'Kennismaking project',
      intervisie: 'Intervisie',
      academie: 'Opleiding (Academie)',
      event: 'Platform-evenement',
      blokkade: 'Eigen tijd',
      reistijd: 'Reistijd',
    } as Record<string, string>)[item.bron],
  });
  const soortLabel =
    item.bron === 'blokkade'
      ? t(`matrix.blokkadeSoort.${item.meta.soort}`, { defaultValue: ({ PRIVE: 'Privé', NETWERK: 'Netwerk / evenement', VAKANTIE: 'Vakantie', ZIEK: 'Ziek / calamiteit' } as Record<string, string>)[item.meta.soort] })
      : null;
  const status = statusLabel();
  const meerdaags = format(start, 'yyyy-MM-dd') !== format(new Date(eind.getTime() - 1), 'yyyy-MM-dd');
  const kanDeelnemen = item.deelnemen_vanaf && new Date(item.deelnemen_vanaf).getTime() <= nu && eind.getTime() > nu;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-gray-500">{soortLabel ?? bronLabel}</p>
          <h3 className="font-semibold text-gray-900 break-words">{item.titel}</h3>
          {item.ondertitel && <p className={`text-sm text-gray-600 ${item.bron === 'medewerker' ? 'font-mono' : ''}`}>{item.ondertitel}</p>}
        </div>
        <button type="button" onClick={onSluiten} className="p-1 rounded hover:bg-gray-100" aria-label={t('plan.sluiten', 'Sluiten')}>
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3 text-sm">
        {status && (
          <span className={`inline-block px-2 py-0.5 rounded border-l-4 text-xs font-medium ${KLEUR_KLASSE[item.kleur]}`} style={blokStijl(item)}>
            {status}
          </span>
        )}
        <div className="flex items-center gap-2 text-gray-700">
          <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {item.hele_dag ? (
            <span className="capitalize">{format(start, 'EEEE d MMMM', { locale: nl })} · {t('matrix.detail.heleDag', 'hele dag')}</span>
          ) : meerdaags ? (
            <span>{format(start, 'EEE d MMM HH:mm', { locale: nl })} – {format(eind, 'EEE d MMM HH:mm', { locale: nl })}</span>
          ) : (
            <span>
              <span className="capitalize">{format(start, 'EEEE d MMMM', { locale: nl })}</span> {format(start, 'HH:mm')}–{format(eind, 'HH:mm')}
            </span>
          )}
        </div>
        {(item.locatie_type || item.locatie) && item.bron !== 'reistijd' && (
          <div className="flex items-center gap-2 text-gray-700">
            {item.locatie_type === 'online' || item.locatie_type === 'ONLINE' ? <Video className="w-4 h-4 text-gray-400" /> : <MapPin className="w-4 h-4 text-gray-400" />}
            <span>
              {item.locatie_type && t(`matrix.locatie.${item.locatie_type}`, {
                defaultValue: ({ ONLINE: 'Online', WERKGEVER: 'Bij de werkgever', EXTERN: 'Externe locatie', online: 'Online', telefonisch: 'Telefonisch', op_locatie: 'Op locatie', fysiek: 'Fysiek' } as Record<string, string>)[item.locatie_type] ?? item.locatie_type,
              })}
              {item.locatie ? ` — ${item.locatie}` : ''}
            </span>
          </div>
        )}
        {item.video_url && (
          <a href={item.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-navy-700 hover:underline">
            <Video className="w-4 h-4" />
            {t('matrix.detail.videolink', 'Open videolink')}
          </a>
        )}

        {item.bron === 'reistijd' && (
          <p className="text-gray-700 flex items-start gap-2">
            <Car className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            {t('matrix.detail.reistijd', {
              defaultValue: '{{min}} minuten reistijd (zone {{zone}}), berekend uit de afstand tussen je kantooradres en de vestiging van de opdrachtgever.',
              min: item.meta.minuten,
              zone: item.meta.zone ?? '–',
            })}
          </p>
        )}

        {item.bron === 'b2b' && (
          <>
            {item.meta.soort && <p className="text-gray-600">{B2B_SOORT_LABEL[item.meta.soort as B2bSoort] ?? item.meta.soort}</p>}
            {item.meta.contactpersoon && <p className="text-gray-700">{t('matrix.detail.contactpersoon', { defaultValue: 'Contactpersoon: {{naam}}', naam: item.meta.contactpersoon })}</p>}
            {item.meta.extern_email && (
              <p className="text-gray-700">
                {t('matrix.detail.extern', {
                  defaultValue: 'Externe genodigde: {{email}} ({{status}})',
                  email: item.meta.extern_email,
                  status: t(`matrix.externStatus.${item.meta.extern_status}`, { defaultValue: ({ UITGENODIGD: 'uitgenodigd', BEVESTIGD: 'bevestigd', GEWEIGERD: 'geweigerd', VERLOPEN: 'verlopen' } as Record<string, string>)[item.meta.extern_status] ?? item.meta.extern_status }),
                })}
              </p>
            )}
            {item.meta.omschrijving && <p className="text-gray-700 whitespace-pre-line">{item.meta.omschrijving}</p>}
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-700 bg-gray-50 rounded-md p-3">
              <dt className="text-gray-500">{t('matrix.detail.uurtarief', 'Uurtarief')}</dt>
              <dd>{item.meta.uurtarief == null ? '—' : euro.format(item.meta.uurtarief)}</dd>
              <dt className="text-gray-500">{t('matrix.detail.urenGepland', 'Uren gepland')}</dt>
              <dd>{item.meta.uren_gepland ?? '—'}</dd>
              <dt className="text-gray-500">{t('matrix.detail.urenGeschreven', 'Uren geschreven')}</dt>
              <dd>{item.meta.uren_geschreven ?? '—'}</dd>
            </dl>
            {item.status === 'AFGEWEZEN' && item.meta.afwijzing_reden && <p className="text-xs text-red-700">{item.meta.afwijzing_reden}</p>}
          </>
        )}

        {item.bron === 'beschikbaar' && (
          <p className="text-gray-600">{t('matrix.detail.beschikbaarUitleg', 'Medewerkers in je pools kunnen deze tijd claimen. Sleep over deze tijd om er direct iets in te plannen.')}</p>
        )}
        {item.bron === 'blokkade' && item.meta.soort !== 'NETWERK' && (
          <p className="text-gray-600">
            {item.meta.soort === 'PRIVE'
              ? t('matrix.detail.priveUitleg', 'Anderen zien alleen dat je niet beschikbaar bent.')
              : t('matrix.detail.afwezigUitleg', 'Je staat in je pools op afwezig; medewerkers gaan naar collega’s in dezelfde pool.')}
          </p>
        )}

        {/* Acties */}
        <div className="flex flex-wrap gap-2 pt-1">
          {item.bron === 'beschikbaar' && !begonnen && (
            <Button size="sm" variant="outline" onClick={() => sluitTijd.mutate()} loading={sluitTijd.isPending}>
              <Trash2 className="w-4 h-4 mr-1" />
              {t('matrix.detail.tijdSluiten', 'Tijd sluiten')}
            </Button>
          )}

          {item.bron === 'medewerker' && item.status === 'PENDING_APPROVAL' && (
            <Link to="/professional/dashboard">
              <Button size="sm">{t('matrix.detail.beoordelen', 'Beoordelen op het dashboard')}</Button>
            </Link>
          )}
          {item.bron === 'medewerker' && (item.status === 'TEGENVOORSTEL' || item.status === 'SCHEDULED') && !begonnen && (
            <Button size="sm" variant="outline" onClick={() => window.confirm(t('matrix.detail.annulerenVraag', 'Afspraak annuleren?')) && annuleer.mutate()} loading={annuleer.isPending}>
              <Ban className="w-4 h-4 mr-1" />
              {t('matrix.detail.annuleren', 'Annuleren')}
            </Button>
          )}
          {item.bron === 'medewerker' && item.status === 'SCHEDULED' && begonnen && (
            <>
              <Button size="sm" onClick={() => voltooid.mutate()} loading={voltooid.isPending}>
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {t('afspraak.voltooid', 'Registreer als voltooid')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => noShow.mutate()} loading={noShow.isPending}>
                <AlertTriangle className="w-4 h-4 mr-1" />
                {t('afspraak.noShow', 'Registreer no-show')}
              </Button>
            </>
          )}
          {item.bron === 'medewerker' && begonnen && item.meta.dossier_id && (
            <Link to={`/professional/dossiers/${item.meta.dossier_id}/verslagen/nieuw?afspraak=${item.ref_id}`} className="inline-flex items-center gap-1 text-xs text-navy-700 hover:underline self-center">
              <FileText className="w-3.5 h-3.5" />
              {t('afspraak.verslag', 'Verslag')}
            </Link>
          )}

          {item.bron === 'b2b' && item.status === 'GEPLAND' && item.meta.bevestiging_status === 'WACHT' && item.meta.voorgesteld_door === 'employer' && (
            <>
              <Button size="sm" onClick={() => bevestig.mutate()} loading={bevestig.isPending}>
                <Check className="w-4 h-4 mr-1" />
                {t('matrix.detail.accepteren', 'Accepteren')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => weiger.mutate()} loading={weiger.isPending}>
                <X className="w-4 h-4 mr-1" />
                {t('matrix.detail.weigeren', 'Weigeren')}
              </Button>
            </>
          )}
          {item.bron === 'b2b' && item.status === 'GEPLAND' && item.meta.bevestiging_status === 'WACHT' && item.meta.voorgesteld_door === 'professional' && (
            <Button size="sm" variant="outline" onClick={() => trekIn.mutate()} loading={trekIn.isPending}>
              <Ban className="w-4 h-4 mr-1" />
              {t('matrix.detail.intrekken', 'Intrekken')}
            </Button>
          )}
          {item.bron === 'b2b' && ['GEPLAND', 'AFGEWEZEN'].includes(item.status ?? '') && item.meta.bevestiging_status === 'BEVESTIGD' && (
            <Button size="sm" disabled={!begonnen} title={begonnen ? undefined : t('matrix.detail.urenNaAanvang', 'Uren indienen kan pas na aanvang')} onClick={() => dienIn.mutate()} loading={dienIn.isPending}>
              <Send className="w-4 h-4 mr-1" />
              {t('matrix.detail.urenIndienen', 'Uren indienen')}
            </Button>
          )}

          {item.bron === 'intervisie' && item.meta.teams && (
            <Button size="sm" disabled={!kanDeelnemen} onClick={() => deelnemen.mutate()} loading={deelnemen.isPending} title={kanDeelnemen ? undefined : t('matrix.detail.inloopVanaf', 'De inloop opent 15 minuten voor aanvang')}>
              <LogIn className="w-4 h-4 mr-1" />
              {t('matrix.detail.deelnemen', 'Deelnemen (inloop)')}
            </Button>
          )}

          {item.bron === 'blokkade' && eind.getTime() > nu && (
            <Button size="sm" variant="outline" onClick={() => verwijderBlokkade.mutate()} loading={verwijderBlokkade.isPending}>
              <Trash2 className="w-4 h-4 mr-1" />
              {t('blokkade.verwijderen', 'Blokkade opheffen')}
            </Button>
          )}

          {item.link && ['intervisie', 'academie', 'event', 'kennismaking', 'b2b'].includes(item.bron) && (
            <Link to={item.link} className="inline-flex items-center gap-1 text-xs text-navy-700 hover:underline self-center">
              <ExternalLink className="w-3.5 h-3.5" />
              {t('matrix.detail.openen', 'Openen')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
